// Copyright 2026, University of Maryland, All Rights Reserved
import { useRef, useState, type FC, type ChangeEvent } from 'react'
import { Loader2, Upload } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
} from '@/components/ui'

interface AddAdditionalDocumentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (label: string, file: File) => Promise<void> | void
}

export const AddAdditionalDocumentDialog: FC<AddAdditionalDocumentDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [label, setLabel] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const reset = () => {
    setLabel('')
    setFile(null)
    setSubmitting(false)
  }

  const handleClose = (next: boolean) => {
    if (submitting) return
    if (!next) reset()
    onOpenChange(next)
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null
    setFile(f)
  }

  const canSubmit = label.trim().length > 0 && file !== null && !submitting

  const handleSubmit = async () => {
    if (!file || !label.trim()) return
    setSubmitting(true)
    try {
      await onSubmit(label.trim(), file)
      reset()
      onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="!w-[480px] !max-w-[90vw] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Add additional document</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 min-w-0">
          <div className="space-y-2 min-w-0">
            <Label htmlFor="additional-doc-label">Document name</Label>
            <Input
              id="additional-doc-label"
              className="w-full"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Vendor SOC 2 Report"
              disabled={submitting}
            />
          </div>

          <div className="space-y-2 min-w-0">
            <Label>File</Label>
            <div className="flex items-center gap-2 min-w-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
              >
                <Upload className="h-4 w-4 mr-2" />
                Choose file
              </Button>
              <span
                className="text-sm text-muted-foreground truncate min-w-0 flex-1"
                title={file?.name}
              >
                {file ? file.name : 'No file selected'}
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Adding...
              </>
            ) : (
              'Add'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
